import { AttendanceDisplayInfo, PlacementStudentV2, PlacementData } from './../../../_models/tutor-reports/tutorreport';
import { TutorReportsHelperService } from '../../../_helpers/tutor-reports-helper/tutor-reports-helper.service';
import { ThemeService } from './../../../_helpers/theming-engine/theme.service';
import { Component, EventEmitter, Input, OnChanges, OnInit, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { EditLearningSectionModalComponent } from '../../tutor-report-timeline/modals/edit-learning-section-modal/edit-learning-section-modal.component';
import { TimelineSectionStudentInfo } from '../../../_models/timeline-models/timeline-section-student-info.model';
import { GenerateStudentAccessModalData } from '../../../_models/grant-student-access/generate-student-access-modal-data.model';
import { GenerateStudentAccessModalComponent } from '../../../students/generate-student-access-modal/generate-student-access-modal.component';
import { PlacementMaintenance } from '../../../_models/maintenance/placement-maintenance.model';
import { ProgressReportListDialogComponent } from '../../tutor-report-flows/tutor-report-leap-v2/dialogs/progress-report-list-dialog/progress-report-list-dialog.component';
import { ProgressReportDto } from '../../tutor-report-flows/tutor-report-leap-v2/models/leap-v2-api.model';
import { MilestoneModalComponent } from '../../tutor-report-flows/tutor-report-leap-v2/dialogs/milestone-modal/milestone-modal.component';

@Component({
    selector: 'group-student-list',
    templateUrl: './group-student-list.component.html',
    styleUrls: ['./group-student-list.component.css'],
    standalone: false
})
export class GroupStudentListComponent implements OnInit, OnDestroy {
  @Input() includeContainerGutter: boolean = true;
  @Input() clientId: number;
  @Output() studentSelected: EventEmitter<PlacementStudentV2> = new EventEmitter<PlacementStudentV2>();
  @Output() onDataChanged = new EventEmitter<boolean>();
  public students: Array<PlacementStudentV2> = new Array<PlacementStudentV2>();
  public placementId: number;
  public placementData: PlacementData;
  public placementMaintenance: PlacementMaintenance;
  private unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public tutorReportsHelperService: TutorReportsHelperService,
    public themeService: ThemeService,
    private router: Router,
    private dialog: MatDialog
    ) { }

  ngOnInit() {
    this.tutorReportsHelperService.placementData$
      .pipe(
        filter((d): d is PlacementData => d != null),
        takeUntil(this.unsubscribe)
      )
      .subscribe(pd => {
        this.placementData = pd;
        this.buildStudentList();
      });

    this.tutorReportsHelperService.placementMaintenance$.pipe(filter((m): m is PlacementMaintenance => m != null),
      takeUntil(this.unsubscribe)
    ).subscribe(pm => {
      this.placementMaintenance = pm;
      this.buildStudentList();
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private buildStudentList(): void {
    // Fed by two independent streams (placementData$ / placementMaintenance$) that can
    // emit in either order — build only once both have arrived.
    if (this.placementData == null || this.placementMaintenance == null) {
      return;
    }
    let studentHolder = this.placementMaintenance.placementStudents;
    studentHolder.forEach(student => {
      if(student.attendanceInformation != null) {
        let items = student.attendanceInformation.split(";");
        let displayList = new Array<AttendanceDisplayInfo>();
        let itemSplit = [];
        items.forEach(item => {
          itemSplit = item.split(":");
          let description = itemSplit[0].trim();
          description = description.replace("cancellation", "(c)");
          if (description != "Sessions End" && description != "Sessions Start") {
            displayList.push({count: itemSplit[1].trim(), description: description });
          }
        });
        student.cancellationAttendanceFound = items.find(i => i.indexOf("cancellation") != -1) != undefined && items.find(i => i.indexOf("cancellation") != -1).length > 0;
        student.attendanceInformationDisplayList = displayList;
      }
    });
    this.students = studentHolder;
    this.placementId = this.placementData.placementID;
  }

  public selectStudent(student: PlacementStudentV2): void {
    if (this.placementData.lessonPlanTypeCode == 'Classic') {
      this.studentSelected.emit(student);
    }
  }

  public test(): void {
  }

  public getBadgeClass(attendance: string): string {
    if (attendance == "Total Sessions") {
        return "badge-info";
    } else if (attendance.indexOf("(c)") != -1) {
      return "badge-danger";
    } else if (attendance == "Present" || attendance == "Late") {
      return "badge-success";
    } else {
      return "badge-warning";
    }
  }

  public viewStudentTimeline(student: PlacementStudentV2): void {
    this.router.navigate(['/timeline', this.placementId], {
      state: {
        placementId: this.placementId,
        timelineStudent: new TimelineSectionStudentInfo(student.studentId, student.studentFirstName + " " + student.studentLastName),
        subject: this.placementData.subject,
        clientId: this.clientId
      } 
    });
  }

  public addLearningSection(student: PlacementStudentV2): void {
    const dialogRef = this.dialog.open(EditLearningSectionModalComponent, {
      data: {
        editMode: true,
        timelineSection: null,
        add: true,
        students: [new TimelineSectionStudentInfo(student.studentId, student.studentFirstName + " " + student.studentLastName)],
        subject: this.placementData.subject,
        placementId: this.placementId,
      },
      panelClass: 'my-timeline-edit-dialog'
    });
    dialogRef.afterClosed().subscribe((reload: boolean) => {
      if (reload) {
        this.onDataChanged.emit(true);
      }
    });
  }
  
  // Reflects whether an ILP is attached to this student's record (from PlacementMaintenance.placementStudents[].hasILP).
  public studentHasIlp(student: PlacementStudentV2): boolean {
    return student?.hasILP ?? false;
  }

  // Total number of progress reports for this student (from PlacementMaintenance.placementStudents[].progressReportCount).
  public getProgressReportCount(student: PlacementStudentV2): number {
    return student?.progressReportCount ?? 0;
  }

  // TODO: not yet implemented — view the existing ILP or create one if none is attached.
  public viewOrCreateIlp(studentId: number, placementId: number): void {
    this.router.navigate(['/individual-learning-plan', placementId, studentId]);
  }

  // Start a NEW progress report — no reportId, so the screen opens a blank, save-ready form.
  public newProgressReport(studentId: number, placementId: number): void {
    this.router.navigate(['/progress-report', placementId, studentId]);
  }

  // View existing progress reports in a tile-list modal; opening a tile navigates to that report.
  public viewProgressReports(studentId: number, placementId: number, studentName: string): void {
    this.dialog.open(ProgressReportListDialogComponent, {
      data: { placementId, studentId, studentName },
      panelClass: 'my-dialog',
      width: '640px'
    }).afterClosed().subscribe((report?: ProgressReportDto) => {
      if (report) {
        this.router.navigate(['/progress-report', placementId, studentId], {
          queryParams: { reportId: report.id }
        });
      }
    });
  }

  // Create a milestone event for this learner. LEAP V2 only (the CTA lives inside the non-Classic block).
  public addMilestone(student: PlacementStudentV2): void {
    this.dialog.open(MilestoneModalComponent, {
      data: {
        placementId: this.placementId,
        studentId: student.studentId,
        studentName: student.studentFirstName + ' ' + student.studentLastName,
      },
      panelClass: 'my-dialog',
      width: '640px'
    }).afterClosed().subscribe((saved?: boolean) => {
      if (saved) {
        this.onDataChanged.emit(true);
      }
    });
  }

  public grantStudentAccess(student: PlacementStudentV2): void {
    let data = new GenerateStudentAccessModalData();
    data.studentId = student.studentId;
    data.showSignUpEmailButton = student.studentHasEmail;
    data.showWebsiteLinkButton = true;

    const dialogRef = this.dialog.open(GenerateStudentAccessModalComponent, {
      data: data,
      panelClass: 'my-dialog'
    });
  }

}
